import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Droplet, Cpu, Trash2, ChevronDown, CloudOff } from "lucide-react";
import { api, type Device } from "~/lib/api";
import { useAllLive, useDeviceLive, isOnline, type DeviceLive } from "~/lib/live";
import { useMe } from "~/lib/auth";
import { useRelativeTime } from "~/lib/time";
import { ValveControl } from "~/components/ValveControl";
import { AppShell } from "~/components/AppShell";
import { Button } from "~/components/ui/Button";
import { Skeleton } from "~/components/ui/Skeleton";
import { Group, GroupHeader } from "~/components/ui/Group";
import { Row, RowSeparator } from "~/components/ui/Row";
import { ConfirmDialog } from "~/components/ui/ConfirmDialog";
import { useToast } from "~/components/ui/Toast";

export const Route = createFileRoute("/_authed/$deviceId")({
  component: DeviceDetailPage,
});

function DeviceDetailPage() {
  const { deviceId } = useParams({ from: "/_authed/$deviceId" });
  const toast = useToast();

  const { data: devices } = useQuery({
    queryKey: ["devices"],
    queryFn: () => api.listDevices(),
  });

  const { data: device } = useQuery({
    queryKey: ["device", deviceId],
    queryFn: () => api.getDevice(deviceId),
  });

  const { data: me } = useMe();
  const allLive = useAllLive();
  const live = useDeviceLive(deviceId);
  const online = isOnline(live);
  const seenLabel = useRelativeTime(live?.lastSeenAt ?? device?.updated_at);
  const actuators = live?.actuators ?? [];

  const displayName = device?.name?.trim() || prettyId(deviceId);
  const isAquagate = device?.device_type === "aquagate";
  const isAdminOrSupport = me?.role === "admin" || me?.role === "support";
  const isOwnerOfThis =
    me?.role === "owner" && device?.owner_id != null && device.owner_id === me.id;

  const canEdit = isAdminOrSupport || isOwnerOfThis;

  return (
    <AppShell back={{ to: "/", label: "Devices" }} bleed>
      <div className="flex-1 lg:flex">
        {}
        <DeviceRail
          devices={devices ?? []}
          activeId={deviceId}
          live={allLive}
        />

        {}
        <div className="flex-1 min-w-0 px-5 sm:px-6 lg:px-10 pt-2 lg:pt-6 pb-8">
          <div className="max-w-[920px] mx-auto lg:mx-0">
            <header className="pb-5">
              <h1 className="text-largeTitle" style={{ color: "var(--label-primary)" }}>
                {displayName}
              </h1>
              <div
                className="text-subhead mt-1 inline-flex items-center gap-1.5"
                style={{ color: "var(--label-secondary)" }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: online ? "var(--system-green)" : "var(--gray-3)",
                    animation: online ? "pulse 1.6s ease-in-out infinite" : undefined,
                  }}
                />
                {online ? "Online" : `Offline · last seen ${seenLabel}`}
              </div>
            </header>

            {isAquagate && actuators.length > 0 && (
              <StateHero actuators={actuators} online={online} seenLabel={seenLabel} />
            )}

            {device && canEdit && <DeviceEditCard device={device} />}

            <div
              className={`mt-6 grid gap-x-8 gap-y-6 ${
                actuators.length > 0 ? "lg:grid-cols-2" : ""
              }`}
            >
              {actuators.length > 0 && (
                <div className="min-w-0">
                  <GroupHeader>Controls</GroupHeader>
                  <Group>
                    {actuators.map((a, i) => (
                      <div key={a.pin}>
                        <ValveControl
                          deviceId={deviceId}
                          pin={a.pin}
                          label={pinLabel(device?.device_type, a.pin)}
                          kind={pinKind(device?.device_type, a.pin)}
                          ackedOn={a.on}
                          lastSeenAt={live?.lastSeenAt ?? 0}
                          online={online}
                          onError={(m) => toast.error(m)}
                        />
                        {i < actuators.length - 1 && <RowSeparator inset={56} />}
                      </div>
                    ))}
                  </Group>
                </div>
              )}

              <div className="min-w-0 space-y-6">
                <div>
                  <GroupHeader>Diagnostics</GroupHeader>
                  <Group>
                    <Row title="Uptime" detail={live?.uptimeS != null ? formatUptime(live.uptimeS) : <DiagSkeleton />} />
                    <RowSeparator inset={16} />
                    <Row
                      title="Signal"
                      detail={
                        live?.rssiDbm != null && live.rssiDbm !== 0
                          ? `${signalLabel(live.rssiDbm)} · ${live.rssiDbm} dBm`
                          : live?.rssiDbm === 0
                            ? "No reading"
                            : <DiagSkeleton />
                      }
                    />
                    <RowSeparator inset={16} />
                    <Row title="Boots" detail={live?.bootCount != null ? String(live.bootCount) : <DiagSkeleton />} />
                  </Group>
                </div>

                <div>
                  <GroupHeader>About</GroupHeader>
                  <Group>
                    <Row
                      title="Device ID"
                      detail={
                        <span className="font-mono text-footnote" style={{ color: "var(--label-secondary)" }}>
                          {deviceId.slice(-12)}
                        </span>
                      }
                    />
                    <RowSeparator inset={16} />
                    <Row title="Type" detail={device?.device_type ? capitalize(device.device_type) : "—"} />
                    <RowSeparator inset={16} />
                    <Row title="Status" detail={device?.status ? capitalize(device.status) : "—"} />
                    {isAdminOrSupport && (
                      <>
                        <RowSeparator inset={16} />
                        <Row
                          title="Owner"
                          detail={
                            device?.owner_email ?? (
                              <span style={{ color: "var(--label-tertiary)" }}>Unclaimed</span>
                            )
                          }
                        />
                      </>
                    )}
                    <RowSeparator inset={16} />
                    <Row
                      title="Added"
                      detail={device?.created_at ? new Date(device.created_at).toLocaleDateString() : "—"}
                    />
                  </Group>
                </div>

                {device && (isAdminOrSupport || isOwnerOfThis) && (
                  <DeviceDangerCard
                    device={device}
                    mode={isAdminOrSupport ? "delete" : "unclaim"}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function DeviceRail({
  devices,
  activeId,
  live,
}: {
  devices: Device[];
  activeId: string;
  live: Record<string, DeviceLive>;
}) {
  if (devices.length === 0) return null;

  return (
    <aside
      className="hidden lg:flex lg:flex-col shrink-0"
      style={{
        width: 280,
        borderRight: "0.5px solid var(--separator)",
        background: "var(--bg-grouped)",
      }}
    >
      <div
        className="px-5 pt-6 pb-3"
        style={{ position: "sticky", top: 0 }}
      >
        <div
          className="text-caption-1 uppercase tracking-[0.06em]"
          style={{ color: "var(--label-tertiary)", fontWeight: 600 }}
        >
          Your Devices
        </div>
      </div>
      <ul className="px-3 pb-6 space-y-0.5 overflow-y-auto">
        {devices.map((d) => {
          const active = d.device_id === activeId;
          const online = isOnline(live[d.device_id]);
          const isAquagate = d.device_type === "aquagate";
          const Icon = isAquagate ? Droplet : Cpu;
          return (
            <li key={d.device_id}>
              <Link
                to="/$deviceId"
                params={{ deviceId: d.device_id }}
                className="flex items-center gap-2.5 px-2.5 py-2 transition-colors"
                style={{
                  borderRadius: 10,
                  background: active ? "var(--fill-tertiary)" : "transparent",
                }}
              >
                <span
                  aria-hidden
                  className="grid place-items-center shrink-0"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: active ? "var(--system-blue)" : "var(--fill-secondary)",
                    color: active ? "#fff" : "var(--label-secondary)",
                  }}
                >
                  <Icon size={14} strokeWidth={2} />
                </span>
                <div className="flex-1 min-w-0">
                  <div
                    className="truncate"
                    style={{
                      color: active ? "var(--label-primary)" : "var(--label-primary)",
                      fontSize: 14,
                      fontWeight: active ? 600 : 500,
                    }}
                  >
                    {d.name?.trim() || prettyId(d.device_id)}
                  </div>
                </div>
                <span
                  aria-hidden
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    background: online ? "var(--system-green)" : "var(--gray-3)",
                  }}
                />
              </Link>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function StateHero({
  actuators,
  online,
  seenLabel,
}: {
  actuators: { pin: number; on: boolean }[];
  online: boolean;
  seenLabel: string;
}) {
  const open = actuators.filter((a) => a.on).length;
  const total = actuators.length;
  const allOpen = open === total && total > 0;
  const allClosed = open === 0;

  const headline = !online
    ? "Device offline"
    : allClosed
      ? "All valves closed"
      : allOpen
        ? `${total === 2 ? "Both" : `All ${total}`} valves open`
        : `${open} of ${total} valves open`;

  const subline = !online
    ? `Last seen ${seenLabel} · controls return when it reconnects`
    : "Tap a control below to change state";

  const tint = !online
    ? "var(--gray-2)"
    : allClosed
      ? "var(--gray-1)"
      : "var(--system-green)";

  const HeroIcon = online ? Droplet : CloudOff;

  return (
    <div
      className="px-5 py-5 flex items-center gap-4"
      style={{
        background: "var(--bg-grouped-secondary)",
        border: "0.5px solid var(--separator)",
        borderRadius: "var(--radius-group)",
      }}
    >
      <span
        aria-hidden
        className="grid place-items-center text-white shrink-0"
        style={{
          width: 60,
          height: 60,
          borderRadius: 18,
          background: tint,
        }}
      >
        <HeroIcon size={28} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-title-2 truncate" style={{ color: "var(--label-primary)" }}>
          {headline}
        </div>
        <div className="text-footnote mt-1" style={{ color: "var(--label-secondary)" }}>
          {subline}
        </div>
      </div>
    </div>
  );
}

function DiagSkeleton() {
  return <Skeleton width={48} height={14} rounded={4} />;
}

function DeviceEditCard({ device }: { device: Device }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(device.name ?? "");
  const [desc, setDesc] = useState(device.description ?? "");

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setName(device.name ?? "");
    setDesc(device.description ?? "");
    setExpanded(false);
  }, [device.device_id, device.name, device.description]);

  const update = useMutation({
    mutationFn: () => api.updateDevice(device.device_id, { name: name.trim(), description: desc.trim() }),
    onSuccess: (saved) => {
      qc.setQueryData(["device", device.device_id], saved);
      qc.setQueryData<Device[]>(["devices"], (cur) =>
        (cur ?? []).map((d) => (d.device_id === saved.device_id ? saved : d)),
      );
      toast.success("Saved.");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Couldn't save the device.");
    },
  });

  const dirty =
    name.trim() !== (device.name ?? "") || desc.trim() !== (device.description ?? "");

  const collapsedHint = name.trim() || "Add a name and description";

  return (
    <section className="mt-6">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        aria-controls={`device-edit-${device.device_id}`}
        className="w-full flex items-center gap-2 px-4 pt-1 pb-2 text-left transition-opacity active:opacity-60"
      >
        <span
          className="text-footnote uppercase tracking-[0.04em]"
          style={{ color: "var(--label-secondary)", fontWeight: 600 }}
        >
          Device
        </span>
        {!expanded && (
          <span
            className="text-footnote truncate"
            style={{
              color: name.trim() ? "var(--label-secondary)" : "var(--label-tertiary)",
              flex: 1,
              minWidth: 0,
              textTransform: "none",
              letterSpacing: 0,
            }}
          >
            · {collapsedHint}
          </span>
        )}
        <span className="flex-1" aria-hidden />
        {dirty && (
          <span
            aria-label="Unsaved changes"
            title="Unsaved changes"
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              background: "var(--system-orange)",
            }}
          />
        )}
        <ChevronDown
          size={16}
          aria-hidden
          style={{
            color: "var(--label-tertiary)",
            transition: "transform 220ms var(--ease-soft)",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="device-edit-body"
            id={`device-edit-${device.device_id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div
              className="px-4 py-4 space-y-3"
              style={{
                background: "var(--bg-grouped-secondary)",
                border: "0.5px solid var(--separator)",
                borderRadius: "var(--radius-group)",
              }}
            >
              <Field
                label="Name"
                value={name}
                onChange={setName}
                placeholder="Give this device a name"
                maxLength={255}
              />
              <Field
                label="Description"
                value={desc}
                onChange={setDesc}
                placeholder="Optional notes (location, owner, etc.)"
                maxLength={1000}
                multiline
              />
              <div className="flex justify-end gap-2 pt-1">
                {dirty && !update.isPending && (
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => {
                      setName(device.name ?? "");
                      setDesc(device.description ?? "");
                    }}
                  >
                    Discard
                  </Button>
                )}
                <Button
                  size="md"
                  onClick={() => update.mutate()}
                  loading={update.isPending}
                  disabled={!dirty || update.isPending}
                >
                  Save
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  multiline = false,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  maxLength?: number;
  multiline?: boolean;
}) {
  const id = `field-${label.replace(/\s+/g, "-").toLowerCase()}`;
  const sharedStyle: React.CSSProperties = {
    background: "var(--fill-tertiary)",
    border: "0.5px solid var(--separator)",
    color: "var(--label-primary)",
    borderRadius: 10,
    paddingInline: 12,
  };
  return (
    <div>
      <label
        htmlFor={id}
        className="text-footnote uppercase tracking-[0.04em] block pb-1.5"
        style={{ color: "var(--label-secondary)", fontWeight: 600 }}
      >
        {label}
      </label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={3}
          className="w-full text-callout focus:outline-none resize-y"
          style={{ ...sharedStyle, paddingBlock: 10, minHeight: 64 }}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          className="w-full text-callout focus:outline-none"
          style={{ ...sharedStyle, height: 40 }}
        />
      )}
    </div>
  );
}

function DeviceDangerCard({
  device,
  mode,
}: {
  device: Device;
  mode: "delete" | "unclaim";
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.removeDevice(device.device_id),
    onSuccess: () => {
      qc.setQueryData<Device[]>(["devices"], (cur) =>
        (cur ?? []).filter((d) => d.device_id !== device.device_id),
      );
      qc.removeQueries({ queryKey: ["device", device.device_id] });
      toast.success(mode === "delete" ? "Device deleted." : "Device unclaimed.");
      navigate({ to: "/" });
    },
    onError: (err) => {
      toast.error(
        err instanceof Error
          ? err.message
          : mode === "delete"
            ? "Couldn't delete this device."
            : "Couldn't unclaim this device.",
      );
    },
  });

  const label = mode === "delete" ? "Delete device" : "Unclaim device";
  const body =
    mode === "delete"
      ? "This removes the device from the system entirely. It will reappear as an unclaimed device the next time it publishes telemetry."
      : "This releases the device so it can be claimed by another account. Your name and description will be cleared.";

  return (
    <section>
      <Group>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-3 px-4 py-3 w-full text-left transition-colors active:bg-[var(--fill-quaternary)]"
        >
          <span
            aria-hidden
            className="grid place-items-center shrink-0 text-white"
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "var(--system-red)",
            }}
          >
            <Trash2 size={15} strokeWidth={2.25} />
          </span>
          <div className="flex-1 min-w-0">
            <div
              className="text-body"
              style={{ color: "var(--system-red)", fontWeight: 500 }}
            >
              {label}
            </div>
          </div>
        </button>
      </Group>

      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={mode === "delete" ? "Delete this device?" : "Unclaim this device?"}
        body={body}
        confirmLabel={label}
        destructive
        onConfirm={() => remove.mutateAsync()}
      />
    </section>
  );
}

function pinLabel(deviceType: string | undefined, pin: number): string {
  if (deviceType === "aquagate") {
    if (pin === 0) return "Valve A";
    if (pin === 1) return "Valve B";
    if (pin === 2) return "AC switch";
  }
  return `Output ${pin + 1}`;
}

function pinKind(deviceType: string | undefined, pin: number): "valve" | "ac" | "generic" {
  if (deviceType === "aquagate") {
    if (pin === 0 || pin === 1) return "valve";
    if (pin === 2) return "ac";
  }
  return "generic";
}

function formatUptime(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function signalLabel(dBm: number): string {
  if (dBm >= -55) return "Strong";
  if (dBm >= -70) return "Good";
  if (dBm >= -85) return "Fair";
  return "Weak";
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function prettyId(deviceId: string) {
  if (!deviceId) return "Device";
  return deviceId.length > 10 ? `Device ${deviceId.slice(-6).toUpperCase()}` : deviceId;
}
